import { AppRoutes } from '../common/constants';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Button,
  Card,
  CardContent,
  CardHeader,
  createFilterOptions,
  Grid,
  InputAdornment,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { BaseRoleplayer, ClientType } from '../common/types/client';
import { Box } from '@mui/system';
import { FnaListItem } from '../common/types/analyses';
import { Icon } from '@iconify/react';
import { JSX, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { makeSignedS3Url } from '../common/helpers/makeSignedS3Url';
import { QuoteItem, QuoteStatus } from '../common/types/quotes';
import { ReportItem } from '../common/types/reports';
import {
  useCreateLegalEntityMutation,
  useCreateNaturalPerson,
  useCreateNewFna,
  useCreateNewReport,
} from '../common/components/mutations';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useQueryMe } from '../common/hooks/auth';
import Content from '../common/components/content';
import Layout from '../common/components/layout';
import QuoteStatusCell from '../quotes/cellRenderers/quoteStatusCell';

// Filter function for the clients autocomplete
const filterClients = createFilterOptions<BaseRoleplayer>({
  matchFrom: 'any',
  stringify: (option) => `${option.roleplayerName} ${option.regNumber || ''}`,
});

// Filter function for the FNAs autocomplete
const filterFna = createFilterOptions<FnaListItem>({
  matchFrom: 'any',
  stringify: (option) => `${option.roleplayerList} ${option.description}`,
});

// Filter function for the Quotes autocomplete
const filterQuotes = createFilterOptions<QuoteItem>({
  matchFrom: 'any',
  stringify: (option) => `${option.policyholder} ${option.lifeinsured}`,
});

// Filter function for the Reports autocomplete
const filterReports = createFilterOptions<ReportItem>({
  matchFrom: 'any',
  stringify: (option) => `${option.name} ${option.desc}`,
});

/**
 * Input for autocomplete component
 */
const AutocompleteInput = (
  parameters: AutocompleteRenderInputParams & TextFieldProps,
): JSX.Element => {
  return (
    <TextField
      {...parameters}
      sx={{
        '& .MuiInputAdornment-root': {
          ml: 2,
          width: '1em',
          height: '1em',
          color: '#101828',
        },
      }}
      InputProps={{
        ...parameters.InputProps,
        type: 'search',
        startAdornment: (
          <InputAdornment position="start">
            <Icon width={16} icon="charm:search" />
          </InputAdornment>
        ),
      }}
    />
  );
};

/**
 * Dashboard page with a widget for each section
 */
const DashboardPage = (): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { data: userData } = useQueryMe();
  const navigate = useNavigate();
  const createFnaMutation = useCreateNewFna();
  const createNaturalPersonMutation = useCreateNaturalPerson();
  const createLegalEntityMutation = useCreateLegalEntityMutation();
  const createReportMutation = useCreateNewReport();

  const { data: clients } = useQuery<BaseRoleplayer[]>({
    queryKey: ['/Roleplayers'],
  });

  const { data: fnaList } = useQuery<FnaListItem[]>({
    queryKey: ['/FNALists'],
  });

  const { data: quotes } = useQuery<QuoteItem[]>({
    queryKey: ['/FNAQuoteLists'],
  });

  const { data: reports } = useQuery<ReportItem[]>({
    queryKey: ['/Reports'],
  });

  const handleCloseCreationMenu = (): void => setAnchorEl(null);

  const handleCreateNaturalPerson = (): void => {
    handleCloseCreationMenu();
    createNaturalPersonMutation.mutate(
      {},
      {
        onSuccess: (data): void => {
          navigate(`${AppRoutes.NaturalPersons}/${data.roleplayerID}/edit`);
        },
      },
    );
  };

  const handleCreateLegalEntity = (): void => {
    handleCloseCreationMenu();
    createLegalEntityMutation.mutate(
      {},
      {
        onSuccess: (data): void => {
          navigate(`${AppRoutes.LegalEntities}/${data.roleplayerID}/edit`);
        },
      },
    );
  };

  const handleCreateFna = (): void => {
    handleCloseCreationMenu();
    createFnaMutation.mutate(
      { stateID: 1 },
      {
        onSuccess: (data): void => {
          navigate(`${AppRoutes.FNAs}/${data.id}`);
        },
      },
    );
  };

  const handleCreateReport = (): void => {
    handleCloseCreationMenu();
    createFnaMutation.mutate(
      {},
      {
        onSuccess: (data): void => {
          navigate(`${AppRoutes.Reports}/${data.id}`);
        },
      },
    );
  };

  return (
    <Layout>
      <Content sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h2">Dashboard</Typography>
            <Typography color="text.secondary">
              Broker name: {userData?.optionalParameters.adviserFirstname}{' '}
              {userData?.optionalParameters.adviserSurname}
            </Typography>
            <Typography color="text.secondary">
              Broker code: {userData?.optionalParameters.adviserBrokerCode} |{' '}
              {userData?.optionalParameters.adviserHouseCode}
            </Typography>
          </Box>
          <Box>
            <LoadingButton
              size="large"
              variant="contained"
              color="secondary"
              endIcon={<Icon icon="uil:plus" />}
              loading={
                createNaturalPersonMutation.isPending ||
                createFnaMutation.isPending ||
                createLegalEntityMutation.isPending ||
                createReportMutation.isPending
              }
              onClick={({ currentTarget }): void => setAnchorEl(currentTarget)}
            >
              New
            </LoadingButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleCloseCreationMenu}>
              <MenuItem onClick={handleCreateLegalEntity}>Client (Legal Entity)</MenuItem>
              <MenuItem onClick={handleCreateNaturalPerson}>Client (Natural Person)</MenuItem>
              <MenuItem onClick={handleCreateFna}>Financial Needs Analysis</MenuItem>
              <MenuItem onClick={handleCreateReport}>Report</MenuItem>
            </Menu>
          </Box>
        </Box>
        <Grid container spacing={6}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardHeader
                title="Total clients"
                avatar={<Icon width={16} icon="uil:users-alt" />}
                action={
                  <Button href={AppRoutes.Clients} variant="outlined" color="primary">
                    View all
                  </Button>
                }
              />
              <CardContent>
                <Typography variant="h1">
                  {clients ? clients.length : <Skeleton variant="text" height={45} width={100} />}
                </Typography>
                <Autocomplete
                  size="small"
                  sx={{ width: 1, mt: 4 }}
                  options={clients ?? []}
                  renderInput={(props): JSX.Element => (
                    <AutocompleteInput {...props} placeholder="Search clients" />
                  )}
                  filterOptions={filterClients}
                  renderOption={(props, option): JSX.Element => (
                    <ListItem {...props}>
                      <ListItemText primary={option.roleplayerName} secondary={option.typeNote} />
                    </ListItem>
                  )}
                  onChange={(_, value): void => {
                    if (value) {
                      navigate(
                        `${
                          value.typeID === ClientType.Individual
                            ? AppRoutes.NaturalPersons
                            : AppRoutes.LegalEntities
                        }/${value.id}`,
                      );
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardHeader
                title="Financial needs analyses"
                avatar={<Icon width={16} icon="uil:users-alt" />}
                action={
                  <Button href={AppRoutes.FNAs} variant="outlined" color="primary">
                    View all
                  </Button>
                }
              />
              <CardContent>
                <Typography variant="h1">
                  {fnaList ? fnaList.length : <Skeleton variant="text" height={45} width={100} />}
                </Typography>
                <Autocomplete
                  size="small"
                  sx={{ width: 1, mt: 4 }}
                  options={fnaList ?? []}
                  renderInput={(props): JSX.Element => (
                    <AutocompleteInput {...props} placeholder="Search FNA's" />
                  )}
                  filterOptions={filterFna}
                  renderOption={(props, option): JSX.Element => (
                    <ListItem {...props}>
                      <ListItemText
                        primary={option.roleplayerList}
                        secondary={option.description}
                      />
                    </ListItem>
                  )}
                  onChange={(_, value): void => {
                    if (value) {
                      navigate(`${AppRoutes.FNAs}/${value.id}`);
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardHeader
                title="Quotes"
                avatar={<Icon width={16} icon="uil:users-alt" />}
                action={
                  <Button href={AppRoutes.Quotes} variant="outlined" color="primary">
                    View all
                  </Button>
                }
              />
              <CardContent>
                <Typography variant="h1">
                  {quotes ? quotes.length : <Skeleton variant="text" height={45} width={100} />}
                </Typography>
                <Autocomplete
                  size="small"
                  multiple // Hack to avoid input value being changed when selecting an option
                  sx={{ width: 1, mt: 4 }}
                  options={quotes ?? []}
                  value={[]}
                  renderInput={(props): JSX.Element => (
                    <AutocompleteInput {...props} placeholder="Search quotes" />
                  )}
                  filterOptions={filterQuotes}
                  getOptionDisabled={(option): boolean => option.statusID !== QuoteStatus.Quoted}
                  renderOption={(props, option): JSX.Element => (
                    <ListItem {...props} secondaryAction={<QuoteStatusCell row={option} />}>
                      <ListItemText
                        primary={
                          <Box className="flex space-x-1">
                            <Typography color="text.secondary">Policy holder:</Typography>
                            <Typography fontWeight={600}>{option.policyholder}</Typography>
                          </Box>
                        }
                        secondary={
                          <Box className="flex space-x-1">
                            <Typography>Life insured:</Typography>
                            <Typography fontWeight={600} color="text.primary">
                              {option.lifeinsured}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  )}
                  onChange={(event, value): void => {
                    if (value?.[0]?.quotePdfUrl) {
                      window.open(makeSignedS3Url(value[0].quotePdfUrl), '_blank');
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardHeader
                title="Reports"
                avatar={<Icon width={16} icon="uil:users-alt" />}
                action={
                  <Button href={AppRoutes.Reports} variant="outlined" color="primary">
                    View all
                  </Button>
                }
              />
              <CardContent>
                <Typography variant="h1">
                  {reports ? reports.length : <Skeleton variant="text" height={45} width={100} />}
                </Typography>
                <Autocomplete
                  size="small"
                  sx={{ width: 1, mt: 4 }}
                  options={reports ?? []}
                  renderInput={(props): JSX.Element => (
                    <AutocompleteInput {...props} placeholder="Search reports" />
                  )}
                  filterOptions={filterReports}
                  renderOption={(props, option): JSX.Element => (
                    <ListItem {...props}>
                      <ListItemText
                        disableTypography
                        primary={<Typography>{option.name || option.id}</Typography>}
                        secondary={
                          <Typography
                            color="text.secondary"
                            variant="caption"
                            className="line-clamp-3"
                          >
                            {option.desc}
                          </Typography>
                        }
                      />
                    </ListItem>
                  )}
                  onChange={(_, value): void => {
                    if (value) {
                      navigate(`${AppRoutes.Reports}/${value.id}`);
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Content>
    </Layout>
  );
};

export default DashboardPage;
